import { Suspense } from "react"
import { makeStyles } from "tss-react/mui"
import { Tooltip, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { FallbackProps } from "react-error-boundary"
import { dinero } from "dinero.js"
import { QueryErrorBoundary, QueryErrorFallback } from "../../../../../core/api/components/QueryErrorBoundary"
import {
    MerchantHomeQuery,
    fetchPosAverageAmount,
    fetchSubscriptionsSales,
    fetchTodaysPosSales,
} from "../merchantHomeApi"
import { DashboardCardSkeleton } from "../../../../dashboards/components/DashboardCardSkeleton"
import { toCurrency, toDinero, toDineroOrUndefined } from "../../../../currency/currencyLib"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { DashboardCard } from "../../../../dashboards/components/DashboardCard"
import { StandardDashboardCard } from "../../components/StandardDashboardCard"
import { MERCHANT_ACCOUNTING_SUBSCRIPTIONS } from "../../../../../constants/routes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { Period } from "../../../../dashboards/dashboardTypes"
import { useMoney } from "../../../../currency/useMoney"
import { MatchEnvironment } from "../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../environment/envTypes"
import { Box } from "@mui/system"

type TodaysPosOrdersType = Readonly<{ merchantId: number }>

export function TodaysPosOrders({ merchantId }: TodaysPosOrdersType) {
    const moneyFactory = useMoney({})

    const todaysPosSales = useApiQuery(fetchTodaysPosSales, {
        queryName: MerchantHomeQuery.TODAYS_POS_SALES,
        dependencies: {
            merchantId: merchantId,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    return (
        <>
            <LocalizedStrict id="merchant-home-number-of-orders-tooltip" attrs={{ title: true }}>
                <Tooltip arrow title="Number of POS orders for today">
                    <Box>
                        <LocalizedStrict id="merchant-home-number-of-orders" attrs={{ title: true }}>
                            <StandardDashboardCard
                                variant="number"
                                title="POS orders"
                                data={todaysPosSales.data?.posSale.numberOfOrders}
                            />
                        </LocalizedStrict>
                    </Box>
                </Tooltip>
            </LocalizedStrict>
            {todaysPosSales.data && (
                <LocalizedStrict id="merchant-home-turnover-tooltip" attrs={{ title: true }}>
                    <Tooltip arrow title="Total POS sales amount for today">
                        <Box>
                            <LocalizedStrict id="merchant-home-turnover" attrs={{ title: true }}>
                                <StandardDashboardCard
                                    variant="number"
                                    title="POS turnover"
                                    subtitle={todaysPosSales.data.posSale.totalAmount.currency}
                                    data={moneyFactory.formatAsNumberWithScale(
                                        toDinero(todaysPosSales.data?.posSale.totalAmount),
                                        "value",
                                    )}
                                />
                            </LocalizedStrict>
                        </Box>
                    </Tooltip>
                </LocalizedStrict>
            )}
        </>
    )
}

// Error Fallback
interface ErrorFallbackProps extends FallbackProps {
    title: string
    subtitle?: string
}

function ErrorFallback({ title, subtitle = "", ...props }: ErrorFallbackProps) {
    return (
        <DashboardCard title={title}>
            <QueryErrorFallback {...props} />
        </DashboardCard>
    )
}

// Cards with Error Fallback

type CardsType = Readonly<{ merchantId: number; title: string }>

export function PosAverageAmount({ merchantId, title }: CardsType) {
    const moneyFactory = useMoney({})
    const posAverageAmountResult = useApiQuery(fetchPosAverageAmount, {
        queryName: MerchantHomeQuery.POS_AVERAGE_AMOUNT,
        dependencies: {
            merchantId: merchantId,
            period: Period.TODAY,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const currency = toCurrency("DKK")
    const zero = dinero({ amount: 0, scale: 2, currency })

    const totalAmount = posAverageAmountResult.data?.posSales[0]
        ? toDineroOrUndefined(posAverageAmountResult.data.posSales[0].salesSummary.totalAmount) ?? zero
        : zero

    return (
        <LocalizedStrict id="merchant-home-avg-amount-tooltip" attrs={{ title: true }}>
            <Tooltip arrow title="Average order amount for todays orders">
                <Box>
                    <StandardDashboardCard
                        variant="number"
                        title={title}
                        subtitle="DKK"
                        data={moneyFactory.formatAsNumber(totalAmount)}
                    />
                </Box>
            </Tooltip>
        </LocalizedStrict>
    )
}

const useStyles = makeStyles()((theme) => ({
    title: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[700],
    },
}))

export function SubscriptionsCard({ merchantId, title }: CardsType) {
    const { classes } = useStyles()
    const navigate = useNavigate()

    const subscriptionsResult = useApiQuery(fetchSubscriptionsSales, {
        queryName: MerchantHomeQuery.TODAYS_SUBSCRIPTIONS_SALES,
        dependencies: {
            merchantId: merchantId,
            period: Period.TODAY,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    function handleClick() {
        navigate(MERCHANT_ACCOUNTING_SUBSCRIPTIONS)
    }

    return (
        <LocalizedStrict id="merchant-home-subscriptions-tooltip" attrs={{ title: true }}>
            <Tooltip arrow title="Today's active lunch subscriptions">
                <Box>
                    <DashboardCard title={title} onClick={handleClick}>
                        <Typography variant="h3" className={classes.title}>
                            {subscriptionsResult.data?.subscriptionSale[0].salesSummary.numberOfSubscriptions || 0}
                        </Typography>
                    </DashboardCard>
                </Box>
            </Tooltip>
        </LocalizedStrict>
    )
}

export function PosSalesCards() {
    const { merchantId } = useMerchant()
    return (
        <>
            <QueryErrorBoundary
                Fallback={(props) => (
                    <>
                        <LocalizedStrict id="merchant-home-number-of-orders" attrs={{ title: true }}>
                            <ErrorFallback {...props} title="Orders" />
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-home-turnover" attrs={{ title: true }}>
                            <ErrorFallback {...props} title="POS turnover" />
                        </LocalizedStrict>
                    </>
                )}
            >
                <Suspense
                    fallback={
                        <>
                            <DashboardCardSkeleton count={2} />
                        </>
                    }
                >
                    <TodaysPosOrders merchantId={merchantId} />
                </Suspense>
            </QueryErrorBoundary>

            <QueryErrorBoundary
                Fallback={(props) => (
                    <LocalizedStrict id="merchant-home-avg-amount" attrs={{ title: true }}>
                        <ErrorFallback {...props} title="Avg. amount" />
                    </LocalizedStrict>
                )}
            >
                <Suspense
                    fallback={
                        <>
                            <DashboardCardSkeleton />
                        </>
                    }
                >
                    <LocalizedStrict id="merchant-home-avg-amount" attrs={{ title: true }}>
                        <PosAverageAmount merchantId={merchantId} title="Avg. amount" />
                    </LocalizedStrict>
                </Suspense>
            </QueryErrorBoundary>

            <QueryErrorBoundary
                Fallback={(props) => (
                    <LocalizedStrict id="merchant-home-subscriptions" attrs={{ title: true }}>
                        <ErrorFallback {...props} title="Subscriptions" />
                    </LocalizedStrict>
                )}
            >
                <Suspense
                    fallback={
                        <>
                            <DashboardCardSkeleton />
                        </>
                    }
                >
                    <LocalizedStrict id="merchant-home-subscriptions" attrs={{ title: true }}>
                        <SubscriptionsCard merchantId={merchantId} title="Subscriptions" />
                    </LocalizedStrict>
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
